import { logger } from './logger';

/**
 * Validates and sanitizes redirect URLs to prevent open redirect vulnerabilities
 *
 * Platform-agnostic implementation that requires the allowed origin to be explicitly provided.
 * This ensures the function can be used in both web and native environments.
 *
 * CVE Mitigated: CVE-2026-22029, CVE-2025-68470 (React Router Open Redirects)
 *
 * @param url - The URL to validate (can be relative or absolute)
 * @param allowedOrigin - The origin that is allowed (e.g., 'https://example.com')
 * @returns Sanitized URL path, or '/' if invalid
 *
 * @example
 * ```typescript
 * // Web usage
 * const redirect = sanitizeRedirectUrl(params.get('redirect'), window.location.origin);
 * navigate(redirect);
 *
 * // React Native usage with config
 * const redirect = sanitizeRedirectUrl(deepLinkUrl, config.appOrigin);
 * navigation.navigate(redirect);
 *
 * // User tries: ?redirect=javascript:alert(1)
 * // Result: navigate('/') - blocked!
 * ```
 */
export function sanitizeRedirectUrl(
  url: string,
  allowedOrigin: string
): string {
  if (!url || !allowedOrigin) {
    return '/';
  }

  try {
    const parsed = new URL(url, allowedOrigin);

    const allowedProtocols = ['http:', 'https:'];
    if (!allowedProtocols.includes(parsed.protocol)) {
      logger.warn(`Blocked redirect with protocol: ${parsed.protocol}`);
      return '/';
    }

    if (parsed.origin !== allowedOrigin) {
      logger.warn(`Blocked cross-origin redirect to: ${parsed.origin}`);
      return '/';
    }

    return parsed.pathname + parsed.search + parsed.hash;
  } catch (error) {
    logger.error('Invalid redirect URL', error, { url });
    return '/';
  }
}

export function isValidUrl(
  urlString: string,
  allowedProtocols: string[] = ['http:', 'https:']
): boolean {
  try {
    const url = new URL(urlString);
    return allowedProtocols.includes(url.protocol);
  } catch {
    return false;
  }
}

export function isAllowedDomain(
  urlString: string,
  allowedDomains: string[]
): boolean {
  try {
    const url = new URL(urlString);
    const hostname = url.hostname;

    return allowedDomains.some((pattern) => {
      if (pattern === hostname) {
        return true;
      }

      if (pattern.startsWith('*.')) {
        const domain = pattern.slice(2);
        return hostname.endsWith(`.${domain}`) || hostname === domain;
      }

      return false;
    });
  } catch {
    return false;
  }
}

export function sanitizeHtml(input: string): string {
  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };

  // char is one of the exact characters matched by the regex above, all present in htmlEscapeMap
  // eslint-disable-next-line security/detect-object-injection
  return input.replace(/[&<>"'/]/g, (char) => htmlEscapeMap[char] || char);
}

export function isSafeFilePath(path: string): boolean {
  if (path.startsWith('/') || /^[a-zA-Z]:/.test(path)) {
    return false;
  }

  if (path.includes('..')) {
    return false;
  }

  if (path.includes('\0')) {
    return false;
  }

  return true;
}

export class SecureApiClient {
  private baseURL: string;
  private allowedOrigin: string;

  constructor(baseURL: string) {
    const url = new URL(baseURL);

    if (!['http:', 'https:'].includes(url.protocol)) {
      throw new Error(`Invalid protocol: ${url.protocol}. Only http: and https: are allowed.`);
    }

    this.baseURL = baseURL;
    this.allowedOrigin = url.origin;
  }

  private validatePath(path: string): void {
    // eslint-disable-next-line no-script-url
    const dangerousSchemes = ['data:', 'javascript:', 'file:', 'vbscript:'];
    if (dangerousSchemes.some((scheme) => path.startsWith(scheme))) {
      throw new Error(`Invalid path scheme detected in: ${path}`);
    }

    if (path.startsWith('http://') || path.startsWith('https://')) {
      throw new Error(`Absolute URLs not allowed. Use relative paths only: ${path}`);
    }

    if (!path.startsWith('/')) {
      throw new Error(`Path must start with /: ${path}`);
    }
  }

  async get<T = unknown>(path: string, options?: RequestInit): Promise<T> {
    this.validatePath(path);

    const response = await fetch(`${this.baseURL}${path}`, {
      method: 'GET',
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async post<T = unknown>(
    path: string,
    body: unknown,
    options?: RequestInit
  ): Promise<T> {
    this.validatePath(path);

    const response = await fetch(`${this.baseURL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(body),
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }
}

export class RateLimiter {
  private requests: Map<string, number[]> = new Map();

  constructor(
    private maxRequests: number,
    private windowMs: number
  ) {}

  tryAcquire(key: string): boolean {
    const now = Date.now();
    const windowStart = now - this.windowMs;

    let timestamps = this.requests.get(key) || [];

    timestamps = timestamps.filter((ts) => ts > windowStart);

    if (timestamps.length >= this.maxRequests) {
      return false;
    }

    timestamps.push(now);
    this.requests.set(key, timestamps);

    return true;
  }

  reset(key: string): void {
    this.requests.delete(key);
  }
}

export function generateCSP(policies: Record<string, string[]>): string {
  return Object.entries(policies)
    .map(([directive, sources]) => {
      const camelToKebab = directive.replace(
        /[A-Z]/g,
        (m) => `-${m.toLowerCase()}`
      );
      return `${camelToKebab} ${sources.join(' ')}`;
    })
    .join('; ');
}

export class PathBuilder {
  private segments: string[] = [];

  constructor(private base = '') {
    if (base) {
      this.segments.push(base);
    }
  }

  append(segment: string | number): PathBuilder {
    const encoded = encodeURIComponent(String(segment));
    this.segments.push(encoded);
    return this;
  }

  build(): string {
    return this.segments.join('/');
  }

  toString(): string {
    return this.build();
  }
}

/**
 * Validates that an OAuth authorization code matches the expected format.
 * Codes are alphanumeric with URL-safe characters, between 4 and 512 characters.
 */
export const isValidAuthCode = (code: string): boolean =>
  /^[a-zA-Z0-9._~-]{4,512}$/.test(code);
